import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import {
  AccessTimeRounded,
  ArrowOutwardRounded,
  AutoAwesomeRounded,
  CalendarMonthRounded,
  DeleteOutlineRounded,
  EditRounded,
  StarRounded,
} from "@mui/icons-material";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import { useNavigate } from "react-router-dom";
import { SurfaceCard } from "../ui/AppPrimitives";
import { getDateTimeLabel, getRelativeDayLabel } from "../utils/dateUtils";

const DatesItem = ({
  dateItem,
  onDelete,
  onPriority,
  celebration = false,
  completed = false,
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const accentColor = celebration ? "#7c3aed" : theme.palette.primary.main;
  const { dateLabel, timeLabel } = getDateTimeLabel(dateItem.dateTime);
  const relativeLabel = getRelativeDayLabel(dateItem.dateTime);
  const [menuAnchor, setMenuAnchor] = useState(null);

  const handleMenuOpen = (event) => {
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  return (
    <SurfaceCard
      sx={{
        p: { xs: 2.6, md: 2.8 },
        border: `1px solid ${alpha(accentColor, 0.1)}`,
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: "0 30px 80px rgba(15, 23, 42, 0.12)",
        },
      }}
    >
      <Stack spacing={2.2}>
        <Stack direction="row" justifyContent="space-between" spacing={2} alignItems="flex-start">
          <Box sx={{ minWidth: 0 }}>
            <Stack
              direction="row"
              spacing={0.9}
              useFlexGap
              flexWrap="wrap"
              sx={{ mb: 1.35, pr: { xs: 0, sm: 1 } }}
            >
              {dateItem.priority && (
                <Chip
                  icon={<StarRounded />}
                  size="small"
                  sx={{
                    bgcolor: "rgba(245, 158, 11, 0.14)",
                    color: "#b45309",
                  }}
                />
              )}
              <Chip
                label={dateItem.category}
                size="small"
                sx={{
                  bgcolor: alpha(accentColor, 0.12),
                  color: accentColor,
                  maxWidth: { xs: "100%", sm: "unset" },
                }}
              />

              <Chip
                label={relativeLabel}
                size="small"
                sx={{
                  bgcolor: alpha(accentColor, 0.08),
                  color: accentColor,
                  fontWeight: 700,
                }}
              />
            </Stack>

            <Typography variant="h4" sx={{ mb: 1 }}>
              {dateItem.title}
            </Typography>

            <Stack
              direction={{ xs: "row", sm: "row" }}
              spacing={{ xs: 0.8, sm: 1.5 }}
              useFlexGap
              flexWrap="wrap"
            >
              <Stack direction="row" spacing={0.8} alignItems="center">
                <CalendarMonthRounded sx={{ fontSize: 18, color: "text.secondary" }} />
                <Typography variant="body2" color="text.secondary">
                  {dateLabel}
                </Typography>
              </Stack>
              {timeLabel && (
                <Stack direction="row" spacing={0.8} alignItems="center">
                  <AccessTimeRounded sx={{ fontSize: 18, color: "text.secondary" }} />
                  <Typography variant="body2" color="text.secondary">
                    {timeLabel}
                  </Typography>
                </Stack>
              )}
            </Stack>
          </Box>

          {!completed && (
            <Stack direction="row" spacing={1} alignItems="center">
              <Tooltip title={dateItem.priority ? "Remove priority" : "Mark as priority"}>
                <IconButton
                  onClick={() => onPriority(dateItem)}
                  sx={{
                    color: dateItem.priority ? "#f59e0b" : "text.secondary",
                    bgcolor: alpha("#ffffff", 0.74),
                    border: "1px solid rgba(148,163,184,0.18)",
                  }}
                >
                  <StarRounded />
                </IconButton>
              </Tooltip>

              <IconButton
                onClick={handleMenuOpen}
                sx={{
                  bgcolor: alpha("#ffffff", 0.74),
                  border: "1px solid rgba(148,163,184,0.18)",
                }}
              >
                <MoreVertRoundedIcon />
              </IconButton>

              <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={handleMenuClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                PaperProps={{
                  sx: {
                    borderRadius: 3,
                    minWidth: 180,
                    mt: 1,
                    boxShadow: "0 16px 40px rgba(15,23,42,.14)",
                  },
                }}
              >
                <MenuItem
                  onClick={() => {
                    handleMenuClose();
                    navigate(`/edit/${dateItem.id}`);
                  }}
                >
                  <ListItemIcon>
                    <EditRounded fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Edit</ListItemText>
                </MenuItem>

                <MenuItem
                  onClick={() => {
                    handleMenuClose();
                    onDelete(dateItem);
                  }}
                  sx={{ color: "error.main" }}
                >
                  <ListItemIcon>
                    <DeleteOutlineRounded color="error" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Delete</ListItemText>
                </MenuItem>
              </Menu>
            </Stack>
          )}
        </Stack>

        {dateItem.notes && (
          <Typography variant="body1" color="text.secondary">
            {dateItem.notes}
          </Typography>
        )}

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.4}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
        >
        </Stack>
      </Stack>
    </SurfaceCard>
  );
};

export default DatesItem;
